// Real-backend HTTP client. Endpoint shapes match /app/docs/openapi.yaml exactly.
import { toast } from "sonner";

const BASE = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || "";

// Guard against the silent-misconfig failure mode where the production build
// forgot to bake a backend URL and every JSON.parse blows up on the HTML
// that Firebase's SPA rewrite hands back.
if (!BASE && typeof window !== "undefined") {
  console.error(
    "%c[IoT Admin Hub] REACT_APP_BACKEND_URL is empty — API calls will hit the frontend origin and fail.",
    "color:#fff;background:#dc2626;padding:2px 6px;border-radius:4px;font-weight:bold"
  );
}

// Backend error envelope codes → user-friendly toast copy.
// Format on the wire: { error: "<code>", message: "<text>" }.
const ERROR_COPY = {
  // 401
  token_expired:  "Your session has expired — please sign in again.",
  invalid_token:  "Session invalid — please sign in again.",
  missing_token:  "You're not signed in.",
  // 403
  read_only:      "Read-only account — writes are disabled for your role.",
  out_of_scope:   "That client isn't in your scope.",
  forbidden:      "You don't have permission to perform this action.",
  // 404
  not_found:      "The requested resource was not found.",
  // 409
  duplicate_username: "That username is already taken.",
  duplicate_mac:      "A device with that MAC already exists.",
  duplicate_name:     "That name is already in use.",
  duplicate_topic:    "That topic already exists.",
  conflict:           "That change conflicts with existing data.",
};

// Only fired once per navigation — avoids toast spam when many parallel
// queries all fail with 401.
let _sessionKilledAt = 0;
function killSession() {
  const now = Date.now();
  if (now - _sessionKilledAt < 1500) return;
  _sessionKilledAt = now;
  try {
    localStorage.removeItem("iot.auth");
  } catch { /* ignore */ }
  // Force the Protected route to bounce to /login on next render tick.
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

function tokenHeader() {
  // Read from the persisted auth store. We touch localStorage directly here
  // (rather than importing the zustand store) so this module stays free of
  // React-only deps and can be called from anywhere.
  try {
    const raw = localStorage.getItem("iot.auth");
    if (!raw) return {};
    const { state } = JSON.parse(raw);
    return state?.token ? { Authorization: `Bearer ${state.token}` } : {};
  } catch (_e) {
    return {};
  }
}

async function http(method, path, { body, query } = {}) {
  const qs = query
    ? "?" +
      Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&")
    : "";

  let res;
  try {
    res = await fetch(`${BASE}${path}${qs}`, {
      method,
      headers: { "Content-Type": "application/json", ...tokenHeader() },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (_netErr) {
    // TypeError: Failed to fetch — DNS, CORS, offline, etc.
    throw new Error(
      "Cannot reach the backend. Check your network connection or the API base URL."
    );
  }

  if (!res.ok) {
    // Try to read a structured error body first.
    // Backend envelope: { error: "<code>", message: "<text>" }.
    let errorCode = "";
    let bodyMsg = "";
    try {
      const j = await res.json();
      errorCode = j.error || "";
      bodyMsg = j.message || ERROR_COPY[errorCode] || j.error || "";
    } catch (_e) {
      /* non-JSON error body */
    }

    // Friendlier messages for the common infrastructure errors.
    if (!bodyMsg) {
      if (res.status === 401) bodyMsg = ERROR_COPY.invalid_token;
      else if (res.status === 403) bodyMsg = ERROR_COPY.forbidden;
      else if (res.status === 404) bodyMsg = `Endpoint not found: ${path}`;
      else if (res.status === 429) bodyMsg = "Too many attempts — try again in a moment";
      else if (res.status === 502 || res.status === 503 || res.status === 504)
        bodyMsg = `Backend unreachable (${res.status}). The iot-admin-hub service may be down or the Nginx upstream is misconfigured.`;
      else bodyMsg = `${res.status} ${res.statusText}`;
    }

    // Global interceptors: side-effects that the callers shouldn't need to
    // repeat. Auth failures kick the user to /login; write/scope failures
    // pop a toast so the UI is honest about *why* the action was rejected.
    if (res.status === 401) {
      // Only kick if the caller wasn't the login endpoint itself.
      if (!path.startsWith("/api/auth/login")) {
        toast.error(bodyMsg);
        killSession();
      }
    } else if (res.status === 403) {
      // `out_of_scope` is not an angry error — it means the operator's JWT
      // doesn't include this client, so the resource is simply "not
      // visible" (per backend contract). Skip the toast; the caller's UI
      // will render an empty state / not-found. Other 403s (forbidden,
      // read_only) do warrant a toast.
      if (errorCode !== "out_of_scope") toast.error(bodyMsg);
    } else if (res.status === 409) {
      toast.error(bodyMsg);
    } else if (res.status >= 500) {
      // Backend blew up (db_error, panic, etc). Surface the real message so
      // users see the SQL error / stack instead of a mysterious empty list.
      toast.error(bodyMsg, { duration: 8000 });
    }
    // 404 is intentionally left silent — pages already surface "not found"
    // states themselves (Device detail, Gateway detail, etc.).

    const err = new Error(bodyMsg);
    err.status = res.status;
    err.code = errorCode;
    throw err;
  }
  if (res.status === 204) return { ok: true };

  // Guard: if the response says JSON but the body starts with "<", we've hit
  // the frontend's own SPA rewrite (Firebase / nginx serves index.html for
  // unknown paths). This means REACT_APP_BACKEND_URL wasn't baked into the
  // build, so calls landed on the FE origin. Turn the "Unexpected token '<'"
  // parse error into a clear diagnostic.
  const raw = await res.text();
  if (raw.trimStart().startsWith("<")) {
    console.error(
      "[IoT Admin Hub] API returned HTML instead of JSON — the frontend is talking to itself.",
      { path, base: BASE || "(empty — env var missing)" }
    );
    throw new Error(
      "Backend URL misconfigured — the app is talking to itself. Rebuild with REACT_APP_BACKEND_URL set."
    );
  }
  try {
    return JSON.parse(raw);
  } catch {
    throw new Error("Backend returned malformed JSON.");
  }
}

const g = (p, q) => http("GET", p, { query: q });
const p = (p2, b) => http("POST", p2, { body: b });
const pu = (p2, b) => http("PUT", p2, { body: b });
const d = (p2) => http("DELETE", p2);

// ----------------- Auth (JWT bearer) -----------------
// POST /api/auth/login → { token, expires_at, user: { username, role } }
// 4-hour token lifetime is enforced server-side; the FE just stores
// `expires_at` and pre-emptively logs the user out when it elapses.
export const authApi = {
  login: (username, password) => p("/api/auth/login", { username, password }),
  logout: () => p("/api/auth/logout", {}),
};

export const clientsApi = {
  list: () => g("/api/clients"),
  create: (data) => p("/api/clients", data),
  update: (id, data) => pu(`/api/clients/${id}`, data),
  remove: (id) => d(`/api/clients/${id}`),
  devices: (id) => g(`/api/clients/${id}/devices`),
  gateways: (id) => g(`/api/clients/${id}/gateways`),
  callbacks: (id) => g(`/api/clients/${id}/callbacks`),
  contacts: (id) => g(`/api/clients/${id}/contacts`),
};

export const contactsApi = {
  list: (q) => g("/api/contacts", q),
  get: (id) => g(`/api/contacts/${id}`),
  create: (data) => p("/api/contacts", data),
  update: (id, data) => pu(`/api/contacts/${id}`, data),
  remove: (id) => d(`/api/contacts/${id}`),
};

export const devicesApi = {
  list: (q) => g("/api/devices", q),
  get: (id) => g(`/api/devices/${id}`),
  create: (data) => p("/api/devices", data),
  update: (id, data) => pu(`/api/devices/${id}`, data),
  remove: (id) => d(`/api/devices/${id}`),
  types: () => g("/api/devices/types"),
  typeCreate: (data) => p("/api/devices/types", data),
  typeUpdate: (id, data) => pu(`/api/devices/types/${id}`, data),
  typeRemove: (id) => d(`/api/devices/types/${id}`),
  fields: (type_id) => g(`/api/devices/fields/${type_id}`),
  telemetry: (id, q) => g(`/api/devices/${id}/telemetry`, q),
};

export const gatewaysApi = {
  hwList: () => g("/api/gateway/hw"),
  hwCreate: (data) => p("/api/gateway/hw", data),
  hwUpdate: (id, data) => pu(`/api/gateway/hw/${id}`, data),
  hwRemove: (id) => d(`/api/gateway/hw/${id}`),
  list: (q) => g("/api/gateways", q),
  create: (data) => p("/api/gateways", data),
  update: (id, data) => pu(`/api/gateways/${id}`, data),
  remove: (id) => d(`/api/gateways/${id}`),
  messages: (q) => g("/api/gateway/messages", q),
  status: (gw_mac) => g(`/api/gateway/status/${gw_mac}`),
};

export const callbacksApi = {
  list: (q) => g("/api/callbacks", q),
  create: (data) => p("/api/callbacks", data),
  update: (id, data) => pu(`/api/callbacks/${id}`, data),
  remove: (id) => d(`/api/callbacks/${id}`),
  test: (body) => p("/api/callbacks/test", body),
};

export const envelopeApi = {
  routes: () => g("/api/envelope/routes"),
  routeCreate: (data) => p("/api/envelope/routes", data),
  routeUpdate: (id, data) => pu(`/api/envelope/routes/${id}`, data),
  routeRemove: (id) => d(`/api/envelope/routes/${id}`),
  dslList: () => g("/api/envelope/dsl"),
  dslSave: (name, data) => pu(`/api/envelope/dsl/${name}`, data),
  dslRemove: (name) => d(`/api/envelope/dsl/${name}`),
  dslValidate: (spec_json) => p("/api/envelope/dsl/validate", { spec_json }),
  scriptsList: () => g("/api/envelope/scripts"),
  scriptSave: (name, data) => pu(`/api/envelope/scripts/${name}`, data),
  scriptRemove: (name) => d(`/api/envelope/scripts/${name}`),
  scriptValidate: (code) => p("/api/envelope/scripts/validate", { code }),
  testParser: (body) => p("/api/envelope/test", body),
};

export const mqttApi = {
  list: () => g("/api/mqtt/topics"),
  create: (data) => p("/api/mqtt/topics", data),
  update: (id, data) => pu(`/api/mqtt/topics/${id}`, data),
  remove: (id) => d(`/api/mqtt/topics/${id}`),
  reload: () => p("/api/mqtt/reload"),
};

export const outputApi = {
  list: () => g("/api/output-destinations"),
  create: (data) => p("/api/output-destinations", data),
  update: (id, data) => pu(`/api/output-destinations/${id}`, data),
  remove: (id) => d(`/api/output-destinations/${id}`),
};

export const pipelinesApi = {
  list: () => g("/api/parser-pipelines"),
  create: (data) => p("/api/parser-pipelines", data),
  update: (id, data) => pu(`/api/parser-pipelines/${id}`, data),
  remove: (id) => d(`/api/parser-pipelines/${id}`),
};

export const systemApi = {
  health: () => g("/healthz"),
  pool: () => g("/pool-stats"),
};

// Users. As of 2026-02-15 the backend still returns numeric `id` and
// numeric `client_ids[]` for this collection (see BACKEND-USERS-ID-PROMPT.md).
// Everything else in the API is string-typed, so we normalise here so the
// rest of the FE can treat user ids uniformly as strings.
const normaliseUser = (u) =>
  u && typeof u === "object"
    ? {
        ...u,
        id: u.id != null ? String(u.id) : u.id,
        client_ids: Array.isArray(u.client_ids) ? u.client_ids.map(String) : u.client_ids,
      }
    : u;

export const usersApi = {
  list: async () => {
    const raw = await g("/api/users");
    return Array.isArray(raw) ? raw.map(normaliseUser) : raw;
  },
  create: async (data) => normaliseUser(await p("/api/users", data)),
  update: (id, data) => pu(`/api/users/${id}`, data),
  remove: (id) => d(`/api/users/${id}`),
  getClients: (id) => g(`/api/users/${id}/clients`),
  setClients: (id, client_ids) =>
    pu(`/api/users/${id}/clients`, { client_ids: (client_ids || []).map(String) }),
};

export const auditApi = {
  // Backend endpoint documented in /app/docs/openapi.yaml. Contract:
  //   GET /api/audit-log?limit=&offset=&username=&resource=&action=
  //   -> { total: number, entries: AuditEntry[] }
  // If the backend hasn't implemented this yet the FE surfaces the 404
  // silently and the page shows a friendly empty state.
  list: ({ limit = 50, offset = 0, username = "", resource = "", action = "" } = {}) => {
    const qs = new URLSearchParams();
    qs.set("limit", limit);
    qs.set("offset", offset);
    if (username) qs.set("username", username);
    if (resource) qs.set("resource", resource);
    if (action) qs.set("action", action);
    return g(`/api/audit-log?${qs.toString()}`);
  },
};

export const telemetryApi = {
  timeseries: (q = { window: "24h" }) => g("/api/telemetry/timeseries", q),
};
